import { Button, Drawer, Input } from '@dnb/eufemia';
import filter from '@dnb/eufemia/icons/dnb/filter';
import { debounce } from '@portals/shared-frontend/utils';
import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import HeroPage from '@/components/HeroPage';
import { HeroContent } from '@/components/HeroPage/HeroContent';
import { HeroContentMenu } from '@/components/HeroPage/HeroContentMenu';
import HeroMenu from '@/components/HeroPage/HeroMenu';

interface ExplorerPageProps {
  title: string;
  filters: ReactNode;
  filterTags: ReactNode;
  children: ReactNode;
}

export function ExplorerPage({ title, filters, filterTags, children }: ExplorerPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useRef<string>(searchParams.get('query') ?? '');
  const isActive = useRef(true);

  useEffect(() => {
    query.current = searchParams.get('query') ?? '';
  }, [searchParams]);

  useEffect(() => {
    return () => {
      isActive.current = false;
    };
  }, []);

  const debounced = useMemo(
    () =>
      debounce((nextQuery: string | undefined) => {
        if (!isActive.current) return;
        if ((nextQuery ?? '') !== query.current) return;

        setSearchParams((prev) => {
          if (nextQuery) {
            prev.set('query', nextQuery);
          } else {
            prev.delete('query');
          }
          prev.set('page', '0');
          return prev;
        });
      }, 300),
    [setSearchParams],
  );

  const searchField = (
    <Input
      clear
      icon="loupe"
      on_change={({ value }) => {
        query.current = value;
        debounced(value);
      }}
      placeholder="Search"
      stretch
      value={query.current}
    />
  );

  return (
    <HeroPage banner="explorer" title={title}>
      <HeroMenu>
        <div className="flex flex-col gap-2">
          {searchField}
          <Button
            className="self-start"
            onClick={() => {
              query.current = '';
              setSearchParams(undefined);
            }}
            variant="tertiary"
          >
            Clear Filters
          </Button>
          {filters}
        </div>
      </HeroMenu>
      <HeroContent>
        <HeroContentMenu>
          {searchField}
          <Drawer
            id="tagFilterDrawer"
            title="Filters"
            top="small"
            triggerAttributes={{ icon: filter, text: 'Filters', variant: 'tertiary' }}
          >
            {filters}
          </Drawer>
          {filterTags}
        </HeroContentMenu>
        {children}
      </HeroContent>
    </HeroPage>
  );
}
